import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

interface Column {
  heading: string;
  items: { label: string; to: string }[];
}

interface MegaMenuProps {
  label: string;
  columns: Column[];
  footer?: { label: string; to: string };
}

/**
 * Desktop hover mega menu (Radwell / DO Supply style).
 * Small delay on close so the pointer can travel from trigger to panel
 * without flicker. Fully keyboard accessible via focus-within.
 */
const MegaMenu = ({ label, columns, footer }: MegaMenuProps) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      <button
        type="button"
        className="flex items-center gap-1 hover:text-brand transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
          role="menu"
        >
          <div
            className="bg-background border border-border rounded-lg shadow-2xl p-6"
            style={{ width: `${Math.min(columns.length * 240 + 48, 960)}px` }}
          >
            <div className={`grid gap-6`} style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
              {columns.map((col) => (
                <div key={col.heading}>
                  <p className="text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    {col.heading}
                  </p>
                  <ul className="space-y-2">
                    {col.items.map((item) => (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className="text-sm text-foreground/85 hover:text-brand transition-colors block"
                          onClick={() => setOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {footer && (
              <div className="border-t border-border mt-5 pt-4">
                <Link
                  to={footer.to}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2.5 transition-all"
                >
                  {footer.label} →
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaMenu;